"use client"
import React, { useState, useRef, useCallback, useEffect } from "react"

export interface SimpleCarouselProps {
  children: React.ReactNode
  className?: string
  trackClassName?: string
  slideClassName?: string
  onSlideChange?: (index: number) => void
  slidesPerView?: number
  activeIndex?: number
  infinite?: boolean
  autoplay?: boolean
  autoplaySpeed?: number
  pauseOnHover?: boolean
  dotsClassName?: string
  renderControls?: (opts: {
    current: number
    count: number
    prev: () => void
    next: () => void
    go: (i: number) => void
  }) => React.ReactNode
}

const Chevron = ({ className }: { className?: string }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 8 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M0.295 10.595L4.875 6.005L0.295 1.415L1.705 0.005L7.705 6.005L1.705 12.005L0.295 10.595Z"
      fill="currentColor"
    />
  </svg>
)

export const SimpleCarousel = ({
  children,
  className,
  trackClassName,
  slideClassName = "px-2",
  onSlideChange,
  slidesPerView = 1,
  activeIndex,
  infinite = true,
  autoplay = false,
  autoplaySpeed = 3000,
  pauseOnHover = true,
  dotsClassName,
  renderControls,
}: SimpleCarouselProps) => {
  const slides = React.Children.toArray(children)
  const count = slides.length
  const maxIndex = Math.max(0, count - slidesPerView)
  const [current, setCurrent] = useState(activeIndex ?? 0)
  const [dragOffset, setDragOffset] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const trackRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)
  const startX = useRef(0)
  const trackWidth = useRef(0)
  const dragging = useRef(false)
  const dragActivated = useRef(false)
  const DRAG_THRESHOLD_PX = 5

  const go = useCallback(
    (index: number) => {
      let next: number
      if (infinite && slidesPerView === 1) {
        next = (index + count) % count
      } else {
        next = Math.max(0, Math.min(index, maxIndex))
      }
      setCurrent(next)
      onSlideChange?.(next)
    },
    [count, maxIndex, slidesPerView, infinite, onSlideChange],
  )

  const prev = useCallback(() => go(current - 1), [go, current])
  const next = useCallback(() => go(current + 1), [go, current])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault()
      prev()
    } else if (e.key === "ArrowRight") {
      e.preventDefault()
      next()
    }
  }

  // Autoplay
  useEffect(() => {
    if (!autoplay || count <= 1) return
    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches
    if (prefersReduced) return
    if (pauseOnHover && isHovered) return

    const id = setInterval(() => go(current + 1), autoplaySpeed)
    return () => clearInterval(id)
  }, [autoplay, autoplaySpeed, pauseOnHover, isHovered, current, count, go])

  // Pause video iframes in NON-active slides only
  useEffect(() => {
    const inner = innerRef.current
    if (!inner) return

    const slideEls = Array.from(inner.children) as HTMLElement[]
    slideEls.forEach((slideEl, i) => {
      const isActive = i >= current && i < current + slidesPerView
      if (isActive) return

      slideEl.querySelectorAll("iframe").forEach(iframe => {
        try {
          const src = iframe.src || ""
          if (src.includes("youtube")) {
            iframe.contentWindow?.postMessage(
              JSON.stringify({ event: "command", func: "pauseVideo" }),
              "*",
            )
          } else if (src.includes("vimeo")) {
            iframe.contentWindow?.postMessage(
              JSON.stringify({ method: "pause", value: "true" }),
              "*",
            )
          } else if (src.includes("vidyard")) {
            iframe.contentWindow?.postMessage(
              JSON.stringify({ event: "command", func: "pause" }),
              "*",
            )
          }
        } catch {
          // cross-origin — ignore
        }
      })
    })
  }, [current, slidesPerView])

  useEffect(() => {
    if (activeIndex !== undefined && activeIndex !== current) {
      const clamped = Math.max(0, Math.min(activeIndex, maxIndex))
      setCurrent(clamped) // eslint-disable-line react-hooks/set-state-in-effect
    }
  }, [activeIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  const onPointerDown = (e: React.PointerEvent) => {
    if (count <= 1) return
    dragging.current = true
    dragActivated.current = false
    startX.current = e.clientX
    trackWidth.current = trackRef.current?.offsetWidth || 1
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return
    const dx = e.clientX - startX.current

    if (!dragActivated.current) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) return
      dragActivated.current = true
      setIsSwiping(true)
      ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    }

    const atStart = current === 0 && dx > 0 && !infinite
    const atEnd = current === maxIndex && dx < 0 && !infinite
    const dampened = atStart || atEnd ? dx * 0.3 : dx
    setDragOffset(dampened)
  }

  const onPointerUp = () => {
    if (!dragging.current) return
    dragging.current = false
    if (!dragActivated.current) return
    dragActivated.current = false
    const threshold = trackWidth.current * 0.2
    if (dragOffset < -threshold) next()
    else if (dragOffset > threshold) prev()
    setDragOffset(0)
    setIsSwiping(false)
  }

  if (count === 0) return null

  const slideWidthPct = 100 / slidesPerView
  const translatePx =
    dragOffset !== 0
      ? `calc(-${current * slideWidthPct}% + ${dragOffset}px)`
      : undefined
  const translatePct =
    dragOffset === 0 ? `-${current * slideWidthPct}%` : undefined

  const defaultControls = count > 1 && (
    <div
      className={[
        "flex items-center justify-center gap-5 lg:gap-2.5 mt-5",
        dotsClassName,
      ]
        .filter(Boolean)
        .join(" ")}
      data-carousel-controls
    >
      <button
        type="button"
        onClick={prev}
        aria-label="Previous slide"
        data-carousel-prev
        className="bg-transparent border-0 cursor-pointer text-gray-m9 p-1 t2-dark:text-white focus-visible:outline-2 focus-visible:outline-black t2-dark:focus-visible:outline-white"
      >
        <Chevron className="rotate-180" />
      </button>

      <div
        className="flex items-center gap-5 lg:gap-2.5"
        role="tablist"
        aria-label="Slide navigation"
      >
        {Array.from({ length: maxIndex + 1 }, (_, i) => (
          <button
            key={i}
            type="button"
            role="tab"
            aria-selected={i === current}
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => go(i)}
            data-carousel-dot
            data-carousel-dot-active={i === current ? "" : undefined}
            className={[
              "w-2 h-2 rounded-full border border-gray-m9 p-0 cursor-pointer t2-dark:border-white",
              i === current ? "bg-gray-m9 t2-dark:bg-white" : "bg-transparent",
            ].join(" ")}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={next}
        aria-label="Next slide"
        data-carousel-next
        className="bg-transparent border-0 cursor-pointer text-gray-m9 p-1 t2-dark:text-white focus-visible:outline-2 focus-visible:outline-black t2-dark:focus-visible:outline-white"
      >
        <Chevron />
      </button>
    </div>
  )

  return (
    <div
      className={["relative select-none", className].filter(Boolean).join(" ")}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      role="region"
      aria-roledescription="carousel"
      aria-label="Media carousel"
      data-carousel
    >
      <div
        ref={trackRef}
        className={["overflow-hidden touch-pan-y", trackClassName]
          .filter(Boolean)
          .join(" ")}
        onDragStart={e => e.preventDefault()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div
          ref={innerRef}
          className={[
            "flex will-change-transform",
            isSwiping ? "" : "transition-transform duration-300 ease-out",
          ].join(" ")}
          style={{ transform: `translateX(${translatePx ?? translatePct})` }}
        >
          {slides.map((slide, i) => {
            const isVisible = i >= current && i < current + slidesPerView
            return (
              <div
                key={i}
                className={["flex-shrink-0", slideClassName]
                  .filter(Boolean)
                  .join(" ")}
                style={{ width: `${slideWidthPct}%` }}
                role="group"
                aria-roledescription="slide"
                aria-label={`Slide ${i + 1} of ${count}`}
                aria-hidden={!isVisible}
                inert={!isVisible ? true : undefined}
              >
                {slide}
              </div>
            )
          })}
        </div>
      </div>

      {/* Live region for screen-reader slide announcements */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {`Slide ${current + 1} of ${count}`}
      </div>

      {renderControls
        ? renderControls({ current, count, prev, next, go })
        : defaultControls}
    </div>
  )
}
